import { Fence, Home as HomeIcon, Car, DoorOpen, ShieldCheck } from "lucide-react";
import {
  PageHero,
  Prose,
  SectionHeading,
  FeatureBand,
  IconCardGrid,
  CtaBand,
  RelatedLinks,
  mailto,
} from "@/components/site/PageParts";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const AVAILABLE = [
  { Icon: Fence, title: "Outdoor arena", body: "A well-maintained outdoor arena suitable for riding and lessons." },
  { Icon: HomeIcon, title: "Stabling", body: "Stabling available on site." }, // TODO: verify with RDA — stabling availability
  { Icon: Car, title: "Parking", body: "On-site parking for riders, coaches and visitors." },
  { Icon: DoorOpen, title: "Clubroom", body: "A small clubroom for gatherings and breaks." }, // TODO: verify with RDA — clubroom availability
];

export default function FacilityHire() {
  return (
    <div data-testid="facility-hire-page">
      <Seo title="Facility Hire — RDA Glasgow" description="Outside of RDA sessions, our Summerston centre is available to hire for riders, coaches and small equestrian groups. Rates on request — get in touch." image={ASSETS.facilityExterior} />
      <PageHero
        title="Facility Hire"
        subtitle="Outside of our RDA sessions, our centre is available to hire."
        image={ASSETS.facilityExterior}
        imageAlt="The RDA Glasgow centre and outdoor facilities in Summerston"
      />

      <Prose>
        <SectionHeading eyebrow="Hire our centre" title="A great space for riders and coaches" />
        <p>
          Outside of RDA sessions, our facilities are available for hire to riders,
          coaches and small equestrian groups. It's a calm, well-kept and accessible
          space — and hiring helps support the charity's work.
        </p>
      </Prose>

      {/* What's available */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="What's available" title="Facilities you can hire" />
          <div className="mt-10">
            <IconCardGrid cards={AVAILABLE} cols={4} />
          </div>
        </div>
      </section>

      {/* Who can hire */}
      <FeatureBand
        image={ASSETS.horsesPaddock}
        imageAlt="Horses in the paddock beside the RDA Glasgow arena"
        title="Who can hire"
        reverse
      >
        <p>
          We welcome individuals, small groups and coaches looking to deliver lessons.
          All hirers must hold appropriate insurance and agree to follow our site rules,
          which are there to keep people and horses safe.
        </p>
      </FeatureBand>

      {/* Rates & booking */}
      <Prose>
        <SectionHeading eyebrow="Rates & booking" title="Rates on request" />
        <p>
          Every booking is a little different, so we keep things simple:
          <strong className="text-rda-ink"> rates are available on request</strong>.
          Please get in touch with a few details about what you need, and we'll be happy
          to help.
        </p>
      </Prose>

      <CtaBand
        title="Interested in hiring our facilities?"
        subtitle="Tell us what you're looking for and we'll get back to you with availability and rates."
        ctaLabel="Enquire about facility hire"
        href={mailto("Facility hire enquiry")}
        testId="facility-hire-cta-band"
      />

      <RelatedLinks
        links={[
          { title: "About Us", description: "Learn more about RDA Glasgow and our centre.", to: "/about" },
          { title: "Support Us", description: "Other ways to support our work.", to: "/support-us" },
          { title: "Contact", description: "Find us and get in touch.", to: "/contact" },
        ]}
      />
    </div>
  );
}
